/* eslint-disable no-console */
import { useEffect, useRef, useState } from 'react';
import intersection from 'lodash/intersection';
import { hideLoading } from '../utils';

const CHUNK = 5;

const useAppHelpers = ({ setFeatures, qbCachedLrnQuestionsLimit, uuid }) => {
  const [appLoaded, setAppLoaded] = useState(true);
  const loadedItemsSetRef = useRef(new Set([]));
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const itemsRaw = useRef(new Set([]));
  const [dynamicItems, setDynamicItems] = useState([]);
  const loadingIndicatorRef = useRef();
  const cbPlayerRef = useRef();
  const [canLoadDynamicItems, setCanLoadDynamicItems] = useState(false);
  const loadingStepIndex = useRef(0);
  const removePreviousItems = useRef(false);

  const getNamespacedReference = (reference) => {
    return `${reference}---${uuid.getNameSpace()}`;
  };

  const questionItemEl = (container, reference) => {
    return container.querySelector(
      `.learnosity-item[data-reference="${getNamespacedReference(reference)}"]`,
    );
  };

  const getActiveHoverReference = (container) => {
    return container.dataset.activequestion;
  };

  const getActiveHoverEl = (container) => {
    return questionItemEl(container, getActiveHoverReference(container));
  };

  useEffect(() => {
    if (canLoadDynamicItems && dynamicItems.length) {
      const newItems = (dynamicItems || []).map(
        ({ learnosity_item_reference }) => learnosity_item_reference,
      );

      if (
        intersection(Array.from(itemsRaw.current), newItems).length ===
        newItems.length
      ) {
        //items are already added, skip
        return;
      }

      let currentItems;
      if (canClearCache(appRef.current, newItems.length)) {
        removePreviousItems.current = true;
        currentItems = [];
        loadedItemsSetRef.current.clear();
        uuid.renew();
      } else {
        currentItems = Array.from(itemsRaw.current);
      }

      itemsRaw.current = new Set([...currentItems, ...newItems]);

      /**
       * We start loading the new questions on if there are NO loading questions on the way
       * this is support for TQs in Course Guide
       * */
      loadingStepIndex.current += 1;
      loadQuestions(loadingStepIndex.current);
    }
  }, [canLoadDynamicItems, dynamicItems]);

  const showQuestionInPipeLine = (_item) => {
    //showQuestion(hoveredQuestionRef.current);
    const item = _item || getActiveHoverEl(containerRef.current);
    if (item) {
      showQuestion(item);
    }
  };

  const setHelperItemIsLoading = (reference) => {
    loadedItemsSetRef.current.add(reference);
  };

  const setHelperProps = (
    app,
    _itemsRaw,
    container,
    loadingIndicator,
    cbPlayer,
  ) => {
    appRef.current = app;
    loadingIndicatorRef.current = loadingIndicator;
    cbPlayerRef.current = cbPlayer;

    itemsRaw.current = new Set(
      (_itemsRaw || []).map(
        ({ learnosity_item_reference }) => learnosity_item_reference,
      ),
    );
    setAppLoaded(false);
    containerRef.current = container;
  };

  const tryDisplayingPlayerQuestion = ({
    reference,
    container,
    loadingIndicatorRef: incomingLoadingIndicatorRef,
    cbPlayerRef: incomingCbPlayerRef,
  }) => {
    containerRef.current = container;
    loadingIndicatorRef.current = incomingLoadingIndicatorRef.current;
    cbPlayerRef.current = incomingCbPlayerRef.current;

    // DRY
    const item = questionItemEl(container, reference);
    if (item) {
      //question already exists in the dom - display it
      showQuestionInPipeLine(item);
    } else if (
      !item &&
      loadedItemsSetRef.current.has(reference) &&
      loadedItemsSetRef.current.size > 1
    ) {
      //question is loading within the chunks
    } else if (!item && appRef.current) {
      //question has to be loaded
      loadQuestion(appRef.current, reference);
    } else if (!item && !appRef.current) {
      //wait for app and then loadQuestion
    }
  };

  const setVisibleItem = (item) => {
    item?.classList?.remove('is-invisible');
    item?.classList?.add('is-visible');
  };

  const showQuestion = (item) => {
    const container = containerRef.current;
    const isContainerInvisible = container?.classList?.contains('invisible');
    if (isContainerInvisible) {
      return;
    }

    //delay executing allowing learnosity async calls to create the dom elements
    hideLoading(container, loadingIndicatorRef.current, cbPlayerRef.current);
    setVisibleItem(item);
    if (item && window.MathJax && item.dataset.beautify !== 'yes') {
      setTimeout(() => {
        window.MathJax.typesetPromise([item]).then(() => {
          item.dataset.beautify = 'yes';
        });
      }, 100);
    }
  };

  /**
   * Determines if the loaded questions limit was reached
   * @param {object} app The learnosity app handler
   * @param {number} newChunkLength The new chunk of questions length to be loaded
   * @returns {boolean} If limit was reached, clear the questions
   * */
  const canClearCache = (app, newChunkLength) => {
    if (!app) {
      return false;
    }
    let clearCache = false;
    if (qbCachedLrnQuestionsLimit) {
      const container = containerRef.current;
      // speed: slow selector
      const questionElems = container?.querySelectorAll(`div[data-reference]`);
      const loadedQuestionsLength =
        (questionElems?.length || 0) + newChunkLength;
      clearCache = loadedQuestionsLength > qbCachedLrnQuestionsLimit;
    }

    return clearCache;
  };

  const loadQuestion = (app, ref) => {
    loadedItemsSetRef.current.add(ref);
    app
      .addItems({
        items: [
          {
            id: getNamespacedReference(ref),
            reference: ref,
          },
        ],
        removePreviousItems: removePreviousItems.current,
      })
      .then((e) => {
        if (e && e.error) {
          console.log(`%c ${e.error}`, 'color: orange', ref);
        } else {
          showQuestionInPipeLine();
          setFeatures(app);
        }
        return true;
      })
      .catch((e) => {
        console.log(`%c catch`, 'color: red', ref, e);
      });
    removePreviousItems.current = false;
  };

  const getNotLoadedQuestions = () => {
    return [...itemsRaw.current].filter(
      (x) => !loadedItemsSetRef.current.has(x),
    );
  };

  const loadQuestions = (stepIndex) => {
    const app = appRef.current;
    const notLoadedItems = getNotLoadedQuestions();

    if (notLoadedItems.length === 0) {
      return;
    }

    const firstItems = notLoadedItems.slice(0, CHUNK);
    firstItems.forEach((item) => {
      loadedItemsSetRef.current.add(item);
    });

    const itemsToAdd = (firstItems || []).map((reference) => {
      return {
        id: getNamespacedReference(reference),
        reference,
      };
    });

    // addItems.then causes infinite loop of requests to LRN items api
    // some wierd combination of calling multiple loadQuestions at the same time and not calling correctly then()
    const timer = setTimeout(() => {
      app
        .addItems({
          items: itemsToAdd,
          removePreviousItems: removePreviousItems.current,
        })
        .then(() => {
          showQuestionInPipeLine();
        })
        .then(() => {
          setFeatures(app);
          //The loading has to continue after addItems has run
          //In case we clear the previous questions, to have time removing the questions from DOM
          if (stepIndex === loadingStepIndex.current) {
            loadQuestions(stepIndex);
          }
        })
        .catch(() => {
          clearTimeout(timer);
        });
      removePreviousItems.current = false;
    }, 500);
  };

  useEffect(() => {
    const app = appRef.current;

    const preventNavigation = (e) => {
      e.preventDefault();
    };

    if (app) {
      //If user hovers really fast while app was not initialized
      //load the hovered question - it will not run for first question set with the init
      const activeHoverReference = getActiveHoverReference(
        containerRef.current,
      );
      if (activeHoverReference) {
        loadQuestion(app, activeHoverReference);
      }

      setCanLoadDynamicItems(true);
      //disable learnosity navigation with addItems()
      //disabling this, also removed a flicker while hovering fast the first question in the list
      app.on('item:beforeunload', preventNavigation);
    }

    return () => {
      if (app) {
        app.off('item:beforeunload', preventNavigation);
      }
    };
  }, [appLoaded]);

  return [
    {
      setDynamicItems,
      setHelperProps,
      tryDisplayingPlayerQuestion,
      setHelperItemIsLoading,
    },
  ];
};

export default useAppHelpers;
